import { useState, useRef, useEffect } from 'react';

export default function Contato() {
  // Estado com apenas os 3 campos obrigatórios
  const [formData, setFormData] = useState({ nome: '', email: '', mensagem: '' });
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('animate-in');
        });
      },
      { threshold: 0.1 }
    );
    const elements = sectionRef.current?.querySelectorAll('.reveal');
    elements?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  // Ao enviar: monta a mensagem e abre o WhatsApp em nova aba
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    // Impede o comportamento padrão do formulário (sem reload, sem POST)
    e.preventDefault();

    // Monta a mensagem no formato exato solicitado
    const mensagemTexto =
      `Nome Completo: ${formData.nome} | E-mail: ${formData.email} | Motivo do Contato: ${formData.mensagem}`;

    // Codifica a mensagem para uso seguro na URL
    const mensagemCodificada = encodeURIComponent(mensagemTexto);

    // Número no formato internacional (Brasil + DDD + número)
    const numero = '5541999379470';

    // Abre o WhatsApp em nova aba com a mensagem já preenchida
    window.open(`https://wa.me/${numero}?text=${mensagemCodificada}`, '_blank');
  };

  return (
    <section id="contato" ref={sectionRef} className="bg-[#0A0A0A] py-28 md:py-36 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="text-center mb-16 reveal opacity-0 translate-y-8 transition-all duration-700">
          <p className="text-[#A3B18A] font-['Montserrat'] text-xs tracking-[4px] uppercase mb-4">
            Entre em Contato
          </p>
          <h2 className="font-['Montserrat'] font-bold text-3xl md:text-4xl lg:text-[52px] text-white">
            Fale com o escritório
          </h2>
          <div className="w-12 h-[2px] bg-[#A3B18A] mx-auto mt-6" />
        </div>

        <div className="flex flex-col lg:flex-row gap-16 lg:gap-20">
          {/* Formulário */}
          <div className="flex-1 reveal opacity-0 translate-y-8 transition-all duration-700 delay-100">
            <form
              id="contato-form"
              onSubmit={handleSubmit}
              className="space-y-5"
            >
              {/* Campo: Nome Completo */}
              <div>
                <label className="block font-['Montserrat'] text-xs tracking-[2px] uppercase text-[#EDEDED]/60 mb-2">
                  Nome Completo
                </label>
                <input
                  type="text"
                  name="nome"
                  value={formData.nome}
                  onChange={handleChange}
                  required
                  placeholder="Seu nome completo"
                  className="w-full bg-transparent border border-[#2F2F2F] rounded-lg px-5 py-4 text-white font-['Montserrat'] text-sm placeholder-[#EDEDED]/30 focus:outline-none focus:border-[#A3B18A] focus:shadow-[0_0_0_3px_rgba(163,177,138,0.1)] transition-all duration-300"
                />
              </div>

              {/* Campo: E-mail */}
              <div>
                <label className="block font-['Montserrat'] text-xs tracking-[2px] uppercase text-[#EDEDED]/60 mb-2">
                  E-mail
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="seu@email.com"
                  className="w-full bg-transparent border border-[#2F2F2F] rounded-lg px-5 py-4 text-white font-['Montserrat'] text-sm placeholder-[#EDEDED]/30 focus:outline-none focus:border-[#A3B18A] focus:shadow-[0_0_0_3px_rgba(163,177,138,0.1)] transition-all duration-300"
                />
              </div>

              {/* Campo: Mensagem */}
              <div>
                <label className="block font-['Montserrat'] text-xs tracking-[2px] uppercase text-[#EDEDED]/60 mb-2">
                  Mensagem
                </label>
                <textarea
                  name="mensagem"
                  value={formData.mensagem}
                  onChange={handleChange}
                  required
                  maxLength={500}
                  rows={5}
                  placeholder="Conte-nos sobre seu projeto..."
                  className="w-full bg-transparent border border-[#2F2F2F] rounded-lg px-5 py-4 text-white font-['Montserrat'] text-sm placeholder-[#EDEDED]/30 focus:outline-none focus:border-[#A3B18A] focus:shadow-[0_0_0_3px_rgba(163,177,138,0.1)] transition-all duration-300 resize-none"
                />
                <p className="text-right font-['Montserrat'] text-xs text-[#EDEDED]/40 mt-1">
                  {formData.mensagem.length}/500
                </p>
              </div>

              {/* Botão: abre WhatsApp com mensagem pronta */}
              <button
                type="submit"
                disabled={formData.mensagem.length > 500}
                className="w-full py-4 bg-[#A3B18A] text-[#0A0A0A] font-['Montserrat'] font-bold text-sm tracking-widest uppercase rounded-full hover:opacity-90 hover:scale-[1.02] transition-all duration-300 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                Enviar Mensagem
              </button>
            </form>
          </div>

          {/* Informações de contato */}
          <div className="lg:w-[380px] reveal opacity-0 translate-x-8 transition-all duration-700 delay-200">
            <div className="space-y-10">
              {/* WhatsApp */}
              <div>
                <p className="font-['Montserrat'] text-xs tracking-[3px] uppercase text-[#A3B18A] mb-4">
                  WhatsApp
                </p>
                <a
                  href="https://wa.me/5541999379470"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-4 group cursor-pointer"
                >
                  <div className="w-12 h-12 flex items-center justify-center rounded-full border border-[#2F2F2F] group-hover:border-[#A3B18A] transition-colors duration-300">
                    <i className="ri-whatsapp-line text-[#A3B18A] text-xl" />
                  </div>
                  <div>
                    <p className="font-['Montserrat'] text-white text-lg group-hover:text-[#A3B18A] transition-colors duration-300">
                      (41) 99937-9470
                    </p>
                    <p className="font-['Montserrat'] text-[#EDEDED]/50 text-sm">Atendimento rápido</p>
                  </div>
                </a>
              </div>

              {/* Área de Atuação */}
              <div>
                <p className="font-['Montserrat'] text-xs tracking-[3px] uppercase text-[#A3B18A] mb-4">
                  Área de Atuação
                </p>
                <div className="space-y-3">
                  {['Curitiba — PR', 'Matinhos — PR'].map((city) => (
                    <div key={city} className="flex items-center gap-3">
                      <div className="w-8 h-8 flex items-center justify-center">
                        <i className="ri-map-pin-line text-[#A3B18A] text-lg" />
                      </div>
                      <p className="font-['Montserrat'] text-[#EDEDED]/80 text-base">{city}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* E-mail */}
              <div>
                <p className="font-['Montserrat'] text-xs tracking-[3px] uppercase text-[#A3B18A] mb-4">
                  E-mail
                </p>
                <a
                  href="mailto:projetos@andremares.arq.br"
                  className="flex items-center gap-4 group cursor-pointer"
                >
                  <div className="w-12 h-12 flex items-center justify-center rounded-full border border-[#2F2F2F] group-hover:border-[#A3B18A] transition-colors duration-300">
                    <i className="ri-mail-line text-[#A3B18A] text-xl" />
                  </div>
                  <p className="font-['Montserrat'] text-[#EDEDED]/80 text-sm group-hover:text-[#A3B18A] transition-colors duration-300">
                    projetos@andremares.arq.br
                  </p>
                </a>
              </div>

              {/* Linha decorativa */}
              <div className="w-full h-[1px] bg-gradient-to-r from-[#A3B18A]/30 to-transparent" />

              <p className="font-['Montserrat'] text-[#EDEDED]/50 text-sm leading-relaxed">
                Para informações sobre projetos, estudos e desenvolvimento de empreendimentos, entre em contato.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
